import { useIsMobile } from '@shared/hooks/useMobile';
import { usePermissions } from '@shared/hooks/usePermissions';
import { faEllipsisV, faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Preparation } from '../../models';

interface FloatingButtonsProps {
  preparation?: Preparation;
  onCancelPicking?: () => void;
  onAdjustDiscrepancy?: () => void;
  className?: string;
}

export const FloatingButtons = ({
  preparation,
  onCancelPicking,
  onAdjustDiscrepancy,
  className = '',
}: FloatingButtonsProps) => {
  const { t } = useTranslation('pickingModule');
  const navigate = useNavigate();
  const { id } = useParams();
  const isMobile = useIsMobile();
  const { hasPermission } = usePermissions();

  // 🎯 Verificar permisos de preparación
  const canPacking = hasPermission('preparations.packing');
  const canRollbackOrders = hasPermission('orders.rollback.from-preparing-to-pending');

  // Handler para el botón de ver packing
  const handleViewPacking = () => {
    navigate(`/order-preparation/packing/${id}`);
  };

  // Verificar si se puede cancelar el picking
  // Status 1 = Disponible para picking y no hay productos con qty_done > 0
  const canCancel = preparation && preparation.status === 1 && preparation.lines.every((line) => line.qty_done === 0);

  // Verificar si se puede ver packing
  // Status 2 o 3 = Picking completado
  const canViewPacking = preparation && (preparation.status === 2 || preparation.status === 3);

  // Ajuste de descuadre: picking disponible o en curso (status 1 o 3)
  const showAdjustDiscrepancy =
    !!preparation && (preparation.status === 1 || preparation.status === 3) && canRollbackOrders;

  // Crear array de acciones
  const actions = [];

  // Agregar acción de ver packing solo si el picking está completado y tiene permiso
  if (canViewPacking && canPacking) {
    actions.push({
      label: t('floating_buttons.view_packing'),
      onClick: handleViewPacking,
      dataTestId: 'view-packing-button',
    });
  }

  if (showAdjustDiscrepancy && onAdjustDiscrepancy) {
    actions.push({
      label: t('floating_buttons.adjust_discrepancy'),
      onClick: onAdjustDiscrepancy,
      dataTestId: 'adjust-discrepancy-button',
    });
  }

  // Agregar acción de cancelar si es posible
  if (canCancel && onCancelPicking) {
    actions.push({
      label: t('floating_buttons.cancel_picking'),
      onClick: onCancelPicking,
      icon: faTimes,
      dataTestId: 'cancel-picking-button',
      isDestructive: true,
    });
  }

  // Si no hay acciones, no renderizar nada
  if (actions.length === 0) {
    return null;
  }

  // Renderizado para Mobile - Dropdown con tres puntos
  if (isMobile) {
    return (
      <div className={`absolute top-3 right-4 z-50 ${className}`}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" data-testid="mobile-picking-menu-trigger">
              <FontAwesomeIcon icon={faEllipsisV} className="!w-4 !h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {actions.map((action, index) => (
              <DropdownMenuItem
                key={index}
                onClick={action.onClick}
                className={`cursor-pointer ${action.isDestructive ? 'text-destructive focus:text-destructive' : ''}`}
                data-testid={action.dataTestId || `mobile-action-${index}`}
              >
                {action.icon && <FontAwesomeIcon icon={action.icon} className="w-4 h-4" />}
                {action.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  // Renderizado para Desktop - Botones inline
  return (
    <div className={`flex gap-2 absolute top-3 right-4 z-50 ${className}`}>
      {actions.map((action, index) => (
        <Button
          key={index}
          size="sm"
          variant="outline"
          onClick={action.onClick}
          data-testid={action.dataTestId || `desktop-action-${index}`}
          className={
            action.isDestructive
              ? 'text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground'
              : ''
          }
        >
          {action.icon && <FontAwesomeIcon icon={action.icon} className="w-4 h-4" />}
          {action.label}
        </Button>
      ))}
    </div>
  );
};
